import { useEffect, useRef, useState } from "react";
import { useForm } from "antd/es/form/Form";
import { Button, Form, Input, Space, Typography } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";
import { loginStore } from "@/store/loginStore";

import Logo from "@/assets/logo.png";
import googleIcon from "@/assets/google.png";
import facebookIcon from "@/assets/facebook.png";
import { validatePassword } from "@/utils/validate";

declare global {
  interface Window {
    FB: any;
    fbAsyncInit: () => void;
    google: any;
  }
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formLogin] = useForm();
  const [formRegister] = useForm();
  const [isRegister, setIsRegister] = useState(false);
  const { setLogin, register, loginWithGoogle, loginWithFacebook, loading } = loginStore();

  const raw = searchParams.get("redirect");
  const redirectUrl =
    raw && raw.startsWith("/") && !raw.startsWith("//") && !raw.startsWith("/login") ? raw : "/";

  // Refs to avoid stale closures in SDK callbacks
  const onGoogleSuccess = useRef<(credential: string) => void>(() => {});
  const onFacebookSuccess = useRef<(accessToken: string) => void>(() => {});

  useEffect(() => {
    onGoogleSuccess.current = async (credential: string) => {
      const success = await loginWithGoogle(credential);
      if (success) navigate(redirectUrl);
    };
    onFacebookSuccess.current = async (accessToken: string) => {
      const success = await loginWithFacebook(accessToken);
      if (success) navigate(redirectUrl);
    };
  }, [loginWithGoogle, loginWithFacebook, navigate, redirectUrl]);

  // Load Google GSI script
  useEffect(() => {
    const initGoogle = () => {
      window.google?.accounts.id.initialize({
        client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
        callback: (response: { credential: string }) => {
          onGoogleSuccess.current(response.credential);
        },
      });
    };

    if (window.google) {
      initGoogle();
      return;
    }

    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.head.appendChild(script);
  }, []);

  // Load Facebook SDK
  useEffect(() => {
    window.fbAsyncInit = () => {
      window.FB.init({
        appId: import.meta.env.VITE_FACEBOOK_APP_ID,
        cookie: true,
        xfbml: true,
        version: "v18.0",
      });
    };

    if (document.getElementById("facebook-jssdk")) return;
    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  const handleLogin = async (values: any) => {
    const success = await setLogin(values);
    if (success) navigate(redirectUrl);
  };

  const handleRegister = async (values: any) => {
    const success = await register(values);
    if (success) navigate(redirectUrl);
  };

  const handleGoogleLogin = () => {
    window.google?.accounts.id.prompt();
  };

  const handleFacebookLogin = () => {
    if (!window.FB) return;
    window.FB.login(
      (response: any) => {
        if (response.authResponse?.accessToken) {
          onFacebookSuccess.current(response.authResponse.accessToken);
        }
      },
      { scope: "public_profile" },
    );
  };

  return (
    <section className="background-gradient">
      <div className="min-w-[80%] bg-white p-5 rounded-xl flex flex-col items-center">
        <img width={150} src={Logo} alt="logo" />
        <Typography.Title className="mt-3 title-gradient">Đăng nhập</Typography.Title>
        {!isRegister ? (
          <Form form={formLogin} layout="vertical" className="w-full" onFinish={handleLogin}>
            <Form.Item name="email" label="Email" rules={[{ required: true, message: "Email không được để trống" }]}>
              <Input size="large" placeholder="Nhập Email" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[{ required: true, message: "Mật khẩu không được để trống" }]}
            >
              <Input.Password size="large" placeholder="Nhập mật khẩu" />
            </Form.Item>

            <Button htmlType="submit" size="large" type="primary" className="w-full" loading={loading}>
              Đăng nhập
            </Button>
          </Form>
        ) : (
          <Form form={formRegister} layout="vertical" className="w-full" onFinish={handleRegister}>
            <Form.Item
              name="email"
              label="Email"
              rules={[{ required: true }, { type: "email", message: "Email không hợp lệ" }]}
            >
              <Input size="large" placeholder="Nhập email" />
            </Form.Item>

            <Form.Item
              name="phone"
              label="Số điện thoại"
              rules={[{ required: false }, { type: "tel", message: "Số điện thoại không hợp lệ" }]}
            >
              <Input size="large" placeholder="Nhập số điện thoại" />
            </Form.Item>

            <Form.Item
              name="password"
              label="Mật khẩu"
              rules={[
                { required: true, message: "Mật khẩu không được để trống" },
                () => ({
                  validator(_, value) {
                    if (!value || validatePassword(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Mật khẩu tối thiểu 8 ký tự, gồm chữ hoa, chữ thường, số và ký tự đặc biệt"),
                    );
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder="Nhập mật khẩu" />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label="Nhập lại mật khẩu"
              rules={[
                { required: true, message: "Nhập lại mật khẩu không được để trống" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Mật khẩu nhập lại không khớp"));
                  },
                }),
              ]}
            >
              <Input.Password size="large" placeholder="Nhập lại mật khẩu" />
            </Form.Item>

            <Button htmlType="submit" size="large" type="primary" className="w-full" loading={loading}>
              Đăng ký
            </Button>
          </Form>
        )}

        {!isRegister ? (
          <Button size="large" type="link" className="mt-2" onClick={() => setIsRegister(true)}>
            Đăng ký
          </Button>
        ) : (
          <Typography.Text className="my-3 font-medium">Hoặc đăng nhập bằng</Typography.Text>
        )}

        <Space className="mt-2">
          <button className="cursor-pointer" onClick={handleGoogleLogin}>
            <img src={googleIcon} alt="google-icon" />
          </button>
          <button className="cursor-pointer" onClick={handleFacebookLogin}>
            <img src={facebookIcon} alt="facebook-icon" />
          </button>
        </Space>
      </div>
    </section>
  );
};

export default LoginPage;
